import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../api";

export default function UserAiDefault({ userID }) {
  const { getUserAiDefault, saveUserAiDefault, getAiBuilderAdapterModels } = useContext(APIContext);
  const [state, setState] = useState(null);
  const [adapter, setAdapter] = useState("");
  const [model, setModel] = useState("");
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelError, setModelError] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let current = true;
    setState(null);
    setError("");
    Promise.resolve().then(() => getUserAiDefault(userID)).then((data) => {
      if (!current) return;
      setState(data);
      setAdapter(data.selection?.adapter || "");
      setModel(data.selection?.model || "");
    }).catch(() => {
      if (current) setError("Could not load this user's AI default.");
    });
    return () => { current = false; };
  }, [userID, retry, getUserAiDefault]);

  useEffect(() => {
    let current = true;
    setModels([]);
    setModelError("");
    if (!adapter) {
      setLoadingModels(false);
      return;
    }
    setLoadingModels(true);
    Promise.resolve().then(() => getAiBuilderAdapterModels(adapter)).then((data) => {
      if (!current) return;
      const list = data.models || [];
      setModels(list);
      setModel((previous) => previous || (list.find((entry) => entry.default) || list[0])?.id || "");
      if (!list.length) setModelError("No models are available for this provider.");
    }).catch(() => {
      if (current) setModelError("Could not load models. Choose the provider again to retry.");
    }).finally(() => {
      if (current) setLoadingModels(false);
    });
    return () => { current = false; };
  }, [adapter, getAiBuilderAdapterModels]);

  const dirty = adapter !== (state?.selection?.adapter || "") || model !== (state?.selection?.model || "");
  const available = state?.available || [];
  const labelFor = (id) => available.find((entry) => entry.id === id)?.label || id;
  const onSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const result = await saveUserAiDefault(userID, adapter ? { adapter, model } : null);
      if (!result.saved) throw new Error("Could not save this user's AI default.");
      setState((previous) => ({ ...previous, selection: result.selection }));
      setAdapter(result.selection?.adapter || "");
      setModel(result.selection?.model || "");
      setSaved(true);
    } catch (cause) {
      setError(cause?.response?.data?.error?.message || "Could not save this user's AI default.");
    } finally {
      setSaving(false);
    }
  };
  const controlClass = "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-50";

  return (
    <section aria-labelledby="user-ai-default-heading" className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
      <h2 id="user-ai-default-heading" className="text-base font-bold text-slate-900">Default AI model</h2>
      <p className="text-sm text-slate-500">Choose the AI website builder model for this account. A user-specific default overrides platform settings. Users with model picker access can still choose a different model per prompt.</p>
      {!state && !error && <p role="status" className="text-sm text-slate-500">Loading AI default…</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}{!state && <button className="ml-2 underline" onClick={() => setRetry((value) => value + 1)}>Retry</button>}</p>}
      {state && <>
        <p className="text-sm text-slate-600">Platform default: <strong>{labelFor(state.platform?.adapter)} · {state.platform?.model}</strong></p>
        <p className="text-sm text-slate-600">Saved setting: <strong>{state.selection ? `${labelFor(state.selection.adapter)} · ${state.selection.model}` : "Use platform default"}</strong></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm font-medium text-slate-700 space-y-2">
            <span>AI provider</span>
            <select aria-label="AI provider" className={controlClass} value={adapter} disabled={saving} onChange={(event) => {
              setAdapter(event.target.value);
              setModel("");
              setSaved(false);
              setError("");
            }}>
              <option value="">Use platform default</option>
              {adapter && !available.some((entry) => entry.id === adapter) && <option value={adapter}>{adapter} (unavailable)</option>}
              {available.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
            </select>
          </label>
          {adapter && <label className="text-sm font-medium text-slate-700 space-y-2">
            <span>AI model</span>
            <select aria-label="AI model" className={controlClass} value={model} disabled={saving || loadingModels || !!modelError} onChange={(event) => { setModel(event.target.value); setSaved(false); }}>
              {!model && <option value="">{loadingModels ? "Loading models…" : "Choose a model"}</option>}
              {model && !models.some((entry) => entry.id === model) && <option value={model}>{model}{!loadingModels ? " (unavailable)" : ""}</option>}
              {models.map((entry) => <option key={entry.id} value={entry.id}>{entry.label || entry.id}</option>)}
            </select>
          </label>}
        </div>
        {modelError && <p role="alert" className="text-sm text-red-700">{modelError}</p>}
        <button className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#623ce7] text-white disabled:opacity-50" disabled={saving || !dirty || (adapter && (loadingModels || !!modelError || !models.some((entry) => entry.id === model)))} onClick={onSave}>
          {saving ? "Saving…" : "Save AI default"}
        </button>
        {saved && <p role="status" className="text-sm text-emerald-700">{state.selection ? "User AI default saved. It will apply to the next builder turn." : "This user now follows the platform default."}</p>}
      </>}
    </section>
  );
}
